import { Box, HStack } from '@terra/ui-styled-system';
import {
  Text,
  Badge,
  BadgeSeverity,
  IconButton,
  Tooltip,
  getStatusSeverity,
} from '@terra/ui-design-system';
import { automationId } from '@terra/shared-utils';
import { Clock01Icon, Message01Icon, ReplayIcon } from '@terra/ui-icons';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import type { HARRequest } from '../../hooks/api/mapHarEntryToHarRequest';
import { RequestsSelectionCheckboxCell } from './RequestsSelectionCheckboxCell';
import { RequestsSelectionCheckboxHeader } from './RequestsSelectionCheckboxHeader';
import { REQUESTS_CONTENT_CHECKBOX_COL_ID } from './requestsReassociateAutomationIds';

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const formatTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  const dateStr = date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
  });
  const timeStr = date.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return `${dateStr} ${timeStr}`;
};

export const createRequestsContentColumnDefs = ({
  onAddToContext,
  onReplay,
  enableCheckboxSelection,
}: {
  onAddToContext: (request: HARRequest) => void;
  onReplay: (request: HARRequest) => void;
  enableCheckboxSelection?: boolean;
}): ColDef<HARRequest>[] => [
  ...(enableCheckboxSelection
    ? [
        {
          headerName: '',
          colId: REQUESTS_CONTENT_CHECKBOX_COL_ID,
          initialWidth: 42,
          minWidth: 42,
          maxWidth: 42,
          pinned: 'left' as const,
          lockPosition: true,
          sortable: false,
          filter: false,
          resizable: false,
          cellRenderer: RequestsSelectionCheckboxCell,
          headerComponent: RequestsSelectionCheckboxHeader,
          cellClass: 'no-row-click',
          suppressNavigable: true,
        } satisfies ColDef<HARRequest>,
      ]
    : []),
  {
    headerName: 'Time',
    field: 'timestamp',
    width: 130,
    cellRenderer: (params: ICellRendererParams<HARRequest>) => {
      if (!params.data) return null;

      return (
        <Text fontSize="xs" color="fg.muted" fontFamily="mono">
          {formatTime(params.data.timestamp)}
        </Text>
      );
    },
  },
  {
    headerName: 'Status',
    field: 'status',
    width: 80,
    cellRenderer: (params: ICellRendererParams<HARRequest>) => {
      if (!params.data) return null;
      const severity = getStatusSeverity(params.data.status);

      return severity ? (
        <BadgeSeverity severity={severity} size="sm">
          {params.data.status}
        </BadgeSeverity>
      ) : (
        <Badge size="sm" variant="emphasis">
          {params.data.status}
        </Badge>
      );
    },
  },
  {
    headerName: 'URL',
    field: 'url',
    flex: 1,
    minWidth: 200,
    cellRenderer: (params: ICellRendererParams<HARRequest>) => {
      if (!params.data) return null;

      return (
        <Box minW={0} {...automationId(`request-row-${params.data.id}`)}>
          <Text
            fontSize="xs"
            color="fg.default"
            fontFamily="mono"
            truncate
            title={params.data.url}
          >
            {params.data.url}
          </Text>
        </Box>
      );
    },
  },
  {
    headerName: 'Duration',
    field: 'responseTime',
    width: 100,
    cellRenderer: (params: ICellRendererParams<HARRequest>) => {
      if (!params.data) return null;

      return (
        <HStack gap="1" alignItems="center">
          <Clock01Icon size={12} />
          <Text fontSize="xs" color="fg.muted">
            {params.data.responseTime.toFixed(0)}ms
          </Text>
        </HStack>
      );
    },
  },
  {
    headerName: 'Size',
    field: 'size',
    width: 80,
    cellRenderer: (params: ICellRendererParams<HARRequest>) => {
      if (!params.data) return null;

      return (
        <Text fontSize="xs" color="fg.muted">
          {formatFileSize(params.data.size)}
        </Text>
      );
    },
  },
  {
    headerName: 'Actions',
    width: 90,
    pinned: 'right',
    suppressNavigable: true,
    cellRenderer: (params: ICellRendererParams<HARRequest>) => {
      if (!params.data) return null;
      const request = params.data;

      return (
        <HStack gap="1" justifyContent="center" alignItems="center" h="full">
          <Tooltip title="Add to Chat">
            <IconButton
              aria-label="Add to Chat"
              size="xs"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onAddToContext(request);
              }}
            >
              <Message01Icon size={14} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Replay">
            <IconButton
              aria-label="Replay"
              size="xs"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onReplay(request);
              }}
            >
              <ReplayIcon size={14} />
            </IconButton>
          </Tooltip>
        </HStack>
      );
    },
    filter: false,
    sortable: false,
  },
];
